<template>
	<template v-if="state.loading">
		<LoadingStorefront />
	</template>
	<template v-else>
		<div class="relative min-h-[50lvh] transition-all group" :class="[fireworksOn ? 'has-fireworks' : '']">
			<div class="max-w-screen-md mx-auto my-8">
				<div
					class="px-4 font-poppins flex gap-4 items-center transition-colors group-[.has-fireworks]:text-white"
				>
					<div
						v-if="result.status === 'COMPLETE' && result.tokens_won > 0 && snap.level === 6"
						class="flex-grow"
					>
						<div class="text-2xl sm:text-3xl font-medium tracking-tight">Congratulations!</div>
						<div class="text-base sm:text-lg">Your prize is below.</div>
					</div>
					<div
						v-else-if="result.status === 'COMPLETE' && result.tokens_won > 0 && snap.level < 6"
						class="flex-grow"
					>
						<div class="text-2xl sm:text-3xl font-medium tracking-tight">Congratulations!</div>
						<div class="text-base sm:text-lg">
							{{ result.tokens_won.toLocaleString() }} of your {{ snap.asset.name }} survived.
						</div>
					</div>
					<div v-else-if="result.status === 'COMPLETE' && result.tokens_won === 0" class="flex-grow">
						<div class="text-2xl sm:text-3xl font-medium tracking-tight">Better luck next time.</div>
						<div class="text-base sm:text-lg">None of your {{ snap.asset.name }} survived.</div>
					</div>
					<div v-else-if="result.status === 'FAILED'" class="flex-grow">
						<div class="text-2xl sm:text-3xl font-medium tracking-tight">Snap Failed</div>
						<div class="text-base sm:text-lg">There was a problem completing your snap.</div>
					</div>
					<div v-else class="flex-grow">
						<div class="text-2xl sm:text-3xl font-medium tracking-tight">Snap In Progress</div>
					</div>

					<div>
						<button
							v-if="result.tokens_won > 0"
							@click="handleReplay"
							:disabled="fireworksOn"
							class="whitespace-nowrap align-content-end md:self-auto justify-self-end opacity-0 enabled:opacity-100 text-sm py-2 pl-2 pr-4 rounded-full bg-zinc-50 border-2 border-zinc-300 text-zinc-950 fah:border-zinc-950 fah:saturate-150 transition-all"
						>
							🧨 Replay
						</button>
					</div>
				</div>
			</div>

			<div class="max-w-screen-md mx-auto my-8">
				<div class="mt-8 pb-8 mb-16 space-y-8">
					<div class="grid grid-cols-2 gap-4 items-center px-4 -translate-x-[2%] md:-translate-x-[6%]">
						<div class="font-poppins text-xl text-center translate-x-[15%] pb-8">
							<div
								class="scale-90 inline-block text-sm uppercase px-6 py-2 rounded-full tracking-normal font-normal bg-zinc-300 text-zinc-800"
							>
								Round {{ snap.level }}
							</div>
						</div>
						<div class="font-poppins text-xl text-center translate-x-[-15%] pb-8">
							<div
								class="scale-105 inline-block text-sm uppercase px-6 py-2 rounded-full tracking-normal font-normal bg-gradient-to-tr"
								:class="[
									snap.prize_pool
										? 'from-[#e8e2b2] via-[#a8811f] to-[#e8e2b2] text-zinc-950 font-bold'
										: 'from-purple-500 to-fuchsia-500 text-white',
								]"
							>
								{{ snap.level < 6 ? `Round ${snap.level + 1}` : 'Your Prize' }}
							</div>
						</div>

						<a
							:href="routeToAsset(snap.asset.storefront.slug, snap.asset.id)"
							class="scale-90 translate-x-[15%] border-2 backdrop-blur-sm border-transparent fah:border-zinc-700 rounded-lg bg-zinc-50/90 group-[.has-fireworks]:bg-zinc-900/60 group-[.has-fireworks]:fah:border-zinc-950 px-4 py-8 fah:-rotate-3 fah:scale-110 fah:z-10 transition-all"
						>
							<div class="text-center">
								<div class="relative max-h-80 mx-auto inline-block">
									<img
										:src="$cdnify(snap.asset.image, 640)"
										:alt="snap.asset.name"
										class="block rounded-md max-h-80"
									/>
									<div
										class="text-center text-sm md:text-lg font-bold text-white bg-gradient-to-tr from-cyan-400 via-purple-500 to-fuchsia-500 aspect-square rounded-full p-0.5 md:p-1 absolute left-2 bottom-2"
									>
										<div
											class="bg-zinc-50 text-zinc-950 rounded-full grid place-items-center size-8 md:size-12"
										>
											&times;{{ result.tokens_entered }}
										</div>
									</div>
								</div>
							</div>
							<div class="text-center mt-2">
								<div class="text-sm md:text-base group-[.has-fireworks]:text-white transition-colors">
									{{ snap.asset.name }}
								</div>
								<div
									class="text-xs md:text-sm text-zinc-400 group-[.has-fireworks]:text-zinc-200 transition-colors"
								>
									{{ snap.asset.storefront.name }}
								</div>
							</div>
						</a>

						<a
							v-if="snap.next"
							:href="routeToAsset(snap.next.asset.storefront.slug, snap.next.asset.id)"
							class="scale-105 translate-x-[-15%] border-2 backdrop-blur-sm shadow-lg shadow-zinc-900/30 px-4 py-8 rounded-lg bg-zinc-50/90 group-[.has-fireworks]:bg-zinc-900/60 fah:rotate-3 fah:scale-125 fah:z-10 transition-all"
							:class="[result.tokens_won > 0 ? 'border-fuchsia-500' : 'border-zinc-400']"
						>
							<div class="text-center">
								<div class="relative max-h-80 mx-auto inline-block">
									<img
										:src="$cdnify(snap.next.asset.image, 640)"
										:alt="snap.next.asset.name"
										class="block rounded-md max-h-80"
										:class="result.tokens_won > 0 ? '' : 'saturate-0'"
									/>
									<div
										class="text-center text-sm md:text-lg font-bold text-white bg-gradient-to-tr aspect-square rounded-full p-0.5 md:p-1 absolute right-2 bottom-2"
										:class="
											result.tokens_won > 0
												? 'from-cyan-400 via-purple-500 to-fuchsia-500 text-zinc-950'
												: 'from-zinc-400 to-zinc-300 text-zinc-500'
										"
									>
										<div
											class="bg-zinc-50 text-zinc-950 rounded-full grid place-items-center size-8 md:size-12"
										>
											&times;{{ result.tokens_won }}
										</div>
									</div>
								</div>
							</div>
							<div class="text-center mt-2">
								<div class="text-sm md:text-base group-[.has-fireworks]:text-white transition-colors">
									{{ snap.next.asset.name }}
								</div>
								<div
									class="text-xs md:text-sm text-zinc-400 group-[.has-fireworks]:text-zinc-200 transition-colors"
								>
									{{ snap.next.asset.storefront.name }}
								</div>
							</div>
						</a>

						<div
							v-else-if="snap.prize_pool && result.tokens_won === 0"
							class="scale-105 translate-x-[-15%] border-2 backdrop-blur-sm bg-zinc-50/90 shadow-lg shadow-zinc-900/30 px-4 py-8 rounded-lg border-zinc-400"
						>
							<div class="text-center">
								<div class="relative max-h-80 mx-auto aspect-[5/7]">
									<div
										class="w-full h-full grid place-content-center text-[4rem] bg-gradient-to-tr from-zinc-400 to-zinc-300 rounded-md saturate-0"
									>
										🥚
									</div>
								</div>
							</div>
							<div class="text-center mt-2">
								<div class="text-sm md:text-base">A goose egg</div>
								<div class="text-xs md:text-sm text-zinc-400">Better luck next time!</div>
							</div>
						</div>

						<a
							:href="routeToAsset(result.prizes[0].asset.storefront.slug, result.prizes[0].asset.id)"
							v-else-if="snap.prize_pool && result.tokens_won === 1"
							class="scale-105 translate-x-[-15%] bg-slate-50/90 border-2 backdrop-blur-sm shadow-lg shadow-zinc-900/30 px-4 py-8 rounded-lg border-[#a8811f] group-[.has-fireworks]:bg-zinc-900/60 fah:rotate-3 fah:scale-125 fah:z-10 transition-all"
						>
							<div class="text-center">
								<div class="relative max-h-80 mx-auto inline-block">
									<img
										:src="$cdnify(result.prizes[0].asset.image, 640)"
										:alt="result.prizes[0].asset.name"
										class="block rounded-md max-h-80"
									/>
									<div
										class="text-center text-sm md:text-lg font-bold bg-gradient-to-tr aspect-square rounded-full p-0.5 md:p-1 absolute right-2 bottom-2 from-[#e8e2b2] via-[#a8811f] to-[#e8e2b2] text-zinc-950"
									>
										<div
											class="bg-zinc-50 text-zinc-950 rounded-full grid place-items-center size-8 md:size-12"
										>
											&times;{{ result.tokens_won }}
										</div>
									</div>
								</div>
							</div>
							<div class="text-center mt-2">
								<div class="text-sm md:text-base group-[.has-fireworks]:text-white transition-colors">
									{{ result.prizes[0].asset.name }}
								</div>
								<div
									class="text-xs md:text-sm text-zinc-400 group-[.has-fireworks]:text-zinc-200 transition-colors"
								>
									{{ result.prizes[0].asset.storefront.name }}
								</div>
							</div>
						</a>

						<a
							href="#details"
							v-else-if="snap.prize_pool && result.tokens_won > 1"
							class="scale-105 translate-x-[-15%] bg-slate-50/90 border-2 backdrop-blur-sm shadow-lg shadow-zinc-900/30 px-4 py-8 rounded-lg border-[#a8811f] group-[.has-fireworks]:bg-zinc-900/60 fah:rotate-3 fah:scale-125 fah:z-10 transition-all"
						>
							<div class="text-center">
								<div class="relative max-h-80 mx-auto aspect-[5/7]">
									<div
										class="w-full h-full grid place-content-center text-[6rem] bg-gradient-to-t from-[#e8e2b2] via-[#a8811f] to-[#e8e2b2] rounded-md"
									>
										🏆
									</div>

									<div
										class="text-center text-sm md:text-lg font-bold bg-gradient-to-tr aspect-square rounded-full p-0.5 md:p-1 absolute right-2 bottom-2 from-[#e8e2b2] via-[#a8811f] to-[#e8e2b2] text-zinc-950"
									>
										<div
											class="bg-zinc-50 text-zinc-950 rounded-full grid place-items-center size-8 md:size-12"
										>
											&times;{{ result.tokens_won }}
										</div>
									</div>
								</div>
							</div>
							<div class="text-center mt-2">
								<div class="text-sm md:text-base group-[.has-fireworks]:text-white transition-colors">
									Multiple prizes
								</div>
								<div
									class="text-xs md:text-sm text-zinc-400 group-[.has-fireworks]:text-zinc-200 transition-colors"
								>
									See below for details
								</div>
							</div>
						</a>
					</div>
				</div>

				<!-- Details -->
				<div class="px-4 mt-24 sm:mt-32" id="details">
					<div class="p-4 md:p-8 bg-zinc-100/90 backdrop-blur-sm rounded-xl">
						<div class="text-2xl font-poppins mb-4">Details</div>
						<div class="grid gap-4">
							<div>
								<div class="text-zinc-700">User</div>
								<div class="inline-flex items-center gap-2 rounded-full bg-zinc-200 py-1 pl-1 pr-4">
									<img
										:src="`https://id.blokpax.com/avatar/@${result.user.username}`"
										:alt="`@${result.user.username}`"
										class="size-8 rounded-full shadow-md"
									/>
									<div>@{{ result.user.username }}</div>
								</div>
							</div>

							<div>
								<div class="text-zinc-700">Status</div>
								<div>{{ result.status }}</div>
							</div>

							<div class="overflow-x-hidden">
								<div class="text-zinc-700">Hash</div>
								<div class="font-mono text-sm text-ellipsis overflow-x-hidden">{{ result.hash }}</div>
							</div>

							<div class="overflow-x-hidden">
								<div class="text-zinc-700">Random Number</div>
								<div class="font-mono text-sm text-ellipsis overflow-x-hidden">
									{{ result.random_number }}
								</div>
							</div>

							<div>
								<div class="text-zinc-700">Requested At</div>
								<div class="">
									{{ new Date(result.created_at).toLocaleString() }}
								</div>
							</div>

							<div>
								<div class="text-zinc-700">Executed At</div>
								<div class="">
									{{ new Date(result.executed_at).toLocaleString() }}
								</div>
							</div>

							<div>
								<div class="text-zinc-700">Snapped (Entered)</div>
								<div class="flex items-center gap-2">
									<img :src="snap.asset.image" class="w-8 rounded-md" />
									<div>
										<a
											:href="routeToAsset(snap.asset.storefront.slug, snap.asset.id)"
											class="block fah:underline"
										>
											{{ result.tokens_entered }}&times; {{ snap.asset.name }}
										</a>
									</div>
								</div>
							</div>

							<div>
								<div class="text-zinc-700">Survived (Won)</div>
								<div class="grid gap-2">
									<div class="flex items-center gap-2" v-if="snap.next">
										<img :src="snap.next.asset.image" class="w-8 rounded-md" />
										<div>
											<a
												:href="
													routeToAsset(snap.next.asset.storefront.slug, snap.next.asset.id)
												"
												class="block fah:underline"
											>
												{{ result.tokens_won }}&times; {{ snap.next.asset.name }}
											</a>
										</div>
									</div>

									<div
										class="flex items-center gap-2"
										v-if="result.prizes"
										v-for="prize in result.prizes"
									>
										<img :src="prize.asset.image" class="w-8 rounded-md" />
										<div>
											<a
												:href="routeToAsset(prize.asset.storefront.slug, prize.asset.id)"
												class="block fah:underline"
											>
												{{ prize.asset.name }}
											</a>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<Fireworks
				ref="fw"
				v-if="!state.loading"
				:autostart="false"
				:options="options"
				:class="[
					fireworksOn ? 'opacity-100' : 'opacity-0',
					'absolute',
					'-z-10',
					'inset-0',
					snap.prize_pool
						? 'bg-gradient-to-t from-[#a8811f] to-black'
						: 'bg-gradient-to-t from-fuchsia-700 via-sky-950 to-black',
					'backdrop-blur-md',
					'transition-all',
				]"
			>
				<div>Slot</div>
			</Fireworks>
		</div>
	</template>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Fireworks, FireworksOptions } from '@fireworks-js/vue'

import router from '@/router'
import { useSnapStore } from '@/stores/SnapStore'
import { Snap, SnapResult } from '@/types/Snap'
import { msleep } from '@/util/sleep'
import LoadingStorefront from '@/components/LoadingStorefront.vue'

const snapStore = useSnapStore()
const route = useRoute()

interface snapResultParams {
	snapId: string
	resultId: string
}

const params = {
	resultId: route.params.resultId,
	snapId: route.params.snapId,
} as snapResultParams

const state = reactive({
	loading: true,
})

const fw = ref<InstanceType<typeof Fireworks>>()
const fireworksOn = ref<boolean>(false)
const options = ref<FireworksOptions>({
	opacity: 0.5,
	sound: {
		enabled: true,
		files: ['/audio/explosion0.mp3', '/audio/explosion1.mp3', '/audio/explosion2.mp3'],
		volume: {
			min: 25,
			max: 25,
		},
	},
	delay: {
		min: 30,
		max: 60,
	},
})

onMounted(async () => {
	await Promise.allSettled([snapStore.load(params.snapId), snapStore.loadResult(params.resultId)])
	state.loading = false
})

async function startFireworks() {
	if (!fw.value) {
		return
	}

	if (result.value.tokens_won <= 0) {
		return
	}

	fireworksOn.value = true

	const tokensWon = result.value.tokens_won
	const toLaunch = snap.value.prize_pool ? 150 : Math.min(150, tokensWon * 10)
	const showLength = snap.value.prize_pool ? 5000 : 1000 * Math.log(tokensWon) + 1000
	const delay = showLength / toLaunch

	for (let i = 0; i < toLaunch; i++) {
		fw.value.launch(1)
		await msleep(delay)
	}

	await msleep(1000)
	await fw.value.waitStop()
	fireworksOn.value = false
}

watch(fw, () => startFireworks())

const snap = computed<Snap | null>((): Snap | null => {
	return snapStore.snaps[params.snapId] ?? null
})

const result = computed<SnapResult | null>((): SnapResult | null => {
	return snapStore.results[params.resultId] ?? null
})

function goToAsset(storefront: string, assetId: string | number) {
	router.push(routeToAsset(storefront, assetId))
}

function routeToAsset(storefront: string, assetId: string | number): string {
	return (
		router.resolve({
			name: 'asset',
			params: {
				slug: storefront,
				assetId: assetId,
			},
		}).href ?? ''
	)
}

async function handleReplay() {
	await startFireworks()
}
</script>
